import { type PropsWithChildren, createContext, useContext, useState, useEffect, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Polyline } from '@react-google-maps/api';
import { Box, Modal, IconButton } from '@mui/material';
import { FiX } from 'react-icons/fi';
import { decode, LatLng } from "@googlemaps/polyline-codec";

import { options } from '../../utility/options';
import { Optional } from '../../types/utility'
import { IWaypointMarker } from '../../logic/api';
import { getIcon } from './icons/get-icon';
import { GoogleMapsMarker, MarkerTooltip } from './google-maps-marker';


function calculateMidpoint(markers?: IWaypointMarker[]): Optional<LatLng> {
  if (!markers || !Array.isArray(markers) || !markers.length) {
    return undefined
  }

  if (markers.length === 1) {
    return {
      lat: markers[0].lat,
      lng: markers[0].lng
    }
  }

  const start = markers[0]
  const end = markers[markers.length - 1]

  return {
    lat: (start.lat + end.lat) / 2,
    lng: (start.lng + end.lng) / 2
  }
}

interface IGoogleMapsContext {
  setMarkers: (markers: IWaypointMarker[]) => void;
  setEncodedPolyline: (encodedPolyline: string) => void;
  setZoom: (value: number) => void;
  openMap: () => void
  isOpen: boolean
  closeMap: () => void
}

const GoogleMapsContext = createContext<Optional<IGoogleMapsContext>>(undefined);

export const useGoogleMaps = (): IGoogleMapsContext => {
  const context = useContext(GoogleMapsContext);
  if (context === undefined) {
    throw new Error('useGoogleMaps was instantiated outside of its provider');
  }

  return context;
};

const DEFAULT_MAP_ZOOM = 11

export const GoogleMapsProvider = ({ children }: PropsWithChildren) => {
  if (!options.googleMapsApiKey) {
    throw new Error("Failed to initiate `GoogleMap` component: There is a missing `GOOGLE_MAPS_API_KEY` environment variable.")
  }
  useJsApiLoader({ googleMapsApiKey: options.googleMapsApiKey });

  const [markers, setMarkersState] = useState<IWaypointMarker[]>();
  const [midpoint, setMidpoint] = useState<LatLng>();
  const [polyline, setPolyline] = useState<LatLng[]>();
  const [isOpen, setIsOpen] = useState(false);
  const [zoom, setZoom] = useState(DEFAULT_MAP_ZOOM);

  useEffect(() => {
    if (markers) {
      setMidpoint(calculateMidpoint(markers))
    }
  }, [markers])

  const openMap = useCallback(() => {
    setIsOpen(true)
  }, [])

  const closeMap = useCallback(() => {
    setIsOpen(false)
    setMarkersState(undefined)
    setPolyline(undefined)
    setZoom(DEFAULT_MAP_ZOOM)
  }, [])

  const setMarkers = useCallback((markers: IWaypointMarker[]) => {
    setMarkersState(markers)
  }, [])

  const setEncodedPolyline = useCallback((encodedPolyline: string) => {
    const decoded = decode(encodedPolyline).map(([lat, lng]) => ({ lat, lng }))
    setPolyline(decoded)
  }, [])

  return (
    <GoogleMapsContext.Provider
      value={{
        openMap,
        isOpen,
        closeMap,
        setMarkers,
        setEncodedPolyline,
        setZoom
      }}
    >
      {children}
      <Modal open={isOpen} onClose={() => closeMap()}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          height: '80%',
          bgcolor: 'background.paper',
          p: 4,
        }}>
          <IconButton
            onClick={() => closeMap()}
            sx={{ position: 'absolute', top: 4, right: 4 }}
            size="small"
          >
            <FiX />
          </IconButton>
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={midpoint}
            zoom={zoom}
            clickableIcons={false}
            onLoad={(map) => {
              if (!polyline || polyline.length < 1) {
                return;
              }

              const bounds = new window.google.maps.LatLngBounds();
              polyline?.forEach(marker => {
                bounds.extend(new window.google.maps.LatLng(marker.lat, marker.lng));
              });
              map.fitBounds(bounds);
            }}
          >
            <Polyline
              path={polyline}
              options={{ strokeColor: "blue", strokeOpacity: 0.8, strokeWeight: 3 }}
            />
            {markers?.map((m, i) => (
              <GoogleMapsMarker
                key={`${m.lat}-${m.lng}-${i}`}
                position={{ lat: m.lat, lng: m.lng }}
                icon={getIcon(m)}
                tooltip={<MarkerTooltip marker={m} />}
              />
            ))}
          </GoogleMap>
        </Box>
      </Modal>
    </GoogleMapsContext.Provider>
  );
}
